import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@material-ui/core";
import { ContentCopy, Download, OpenInNew } from "@material-ui/icons";
import ForwardSendIcon from "../../ForwardSendIcon";
import Label, { LabelColor } from "../../Label";
import type { SignatureDocument } from "../../../@types/signatures";
import { downloadPreparedPdfBlob, sendSigningSet } from "../../../services/signatures";
import { buildSigningLinkWhatsappShare, deliveryRecipientLabel } from "../../../utils/signatureWhatsappShare";
import { mergePdfBlobs } from "../../../utils/mergePdfBlobs";
import { carriesQuestionnaireStep, questionnaireListedForOf } from "../../../utils/signatureQuestionnaireStep";
import { questionnaireLabelContext } from "../../../utils/signatureQuestionnaireName";
import type { PreparedSignatureResult } from "./PreparedSignatureDialog";
import { signingLinkOf } from "./preparedSigningLinks";
import { QuestionnaireRidesHere } from "./QuestionnaireStepLabel";
import SigningLinkWhatsappShareButton from "./SigningLinkWhatsappShare";

type Delivery = "idle" | "sending" | "sent" | "failed";

const DELIVERY_COLOR: Record<Delivery, LabelColor> = { idle: "default", sending: "info", sent: "success", failed: "error" };

function initialDelivery(document: SignatureDocument): Delivery {
  return document.signers[0]?.whatsappDelivery?.status === "sent" ? "sent" : "idle";
}

/** How the realtor recognises a contract here: the same "template · number" line as the single dialog. */
function documentLabel(document: SignatureDocument): string {
  return `${document.templateNameSnapshot} · ${document.formattedNumber}`;
}

/** The server's reason a set send was refused (`errorCode` on a 404/409), when there is one. */
function refusalOf(error: unknown): string | undefined {
  return (error as { response?: { data?: { errorCode?: string } } })?.response?.data?.errorCode;
}

/** Hands a file to the browser's downloads. */
function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  window.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Let the browser start consuming the download before releasing its URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * The prepared dialog for a SET of contracts (one bundle preparation: a seller's brokerage order +
 * exclusivity, a saved bundle). The set is ONE unit with ONE link (Trello #231 item 6; Haim,
 * 2026-10-21: "make it clear there is ONE link to send to the customer. Show the 2 lines for the docs,
 * but only ONE set of buttons"). The contracts are plain lines (template · number · state) and the
 * dialog has the single dialog's five actions, once, for the whole set:
 * - "שליחה אוטומטית" sends one WhatsApp + one SMS carrying the first contract's link
 *   (`sendSigningSet`, server signatureSetSend.service.ts); after the SMS code the client's sign page
 *   lists every contract, and signing one returns to the list;
 * - "שיתוף בוואטסאפ" / "העתקת קישור" hand that same one link, with a line saying how many documents it opens;
 * - "החתמה במקום" opens the first contract on the realtor's device; once it is signed, the sign page
 *   offers the next contract of the set (owner-only, `InPersonSetContinue`), each still with its own
 *   explicit skip or code;
 * - "הורדת PDF להדפסה" downloads ONE file with every contract in order (each the owner's prepared-PDF
 *   download: hash-checked, prefills only, no signatures), named by their numbers ("C-12+C-13.pdf").
 * Finishing with part of the set neither sent nor shared asks first. A single contract keeps
 * `PreparedSignatureDialog`'s own body.
 */
export default function PreparedSignatureSetDialog({ result, onClose }: {
  result: PreparedSignatureResult;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const documents = result.documents;
  const [delivery, setDelivery] = useState<Record<string, Delivery>>(() =>
    Object.fromEntries(documents.map((document) => [document.id, initialDelivery(document)])));
  // Contracts the realtor is delivering personally: copied, shared or opened in person (the one link
  // and the in-person session both reach the whole set).
  const [sharedByRealtor, setSharedByRealtor] = useState<string[]>([]);
  const [busy, setBusy] = useState<"message" | "copy" | "download" | null>(null);
  const inFlight = useRef(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);

  const withLink = documents.filter((document) => signingLinkOf(document));
  // The set's one link is its first contract's; every message about the set carries that one.
  const entry = withLink[0];
  const entryLink = entry ? signingLinkOf(entry) : undefined;
  const share = buildSigningLinkWhatsappShare(withLink, t);
  const allSent = withLink.length > 0 && withLink.every((document) => delivery[document.id] === "sent");
  const anySent = documents.some((document) => delivery[document.id] === "sent");
  const anyFailed = documents.some((document) => delivery[document.id] === "failed");
  const notDelivered = documents.filter((document) =>
    delivery[document.id] !== "sent" && !sharedByRealtor.includes(document.id));
  // Started sharing the set and left part of it behind: the case where a contract was forgotten.
  const mustConfirmClose = notDelivered.length > 0 && (anySent || anyFailed || sharedByRealtor.length > 0);
  const canDownload = documents.length > 0 && documents.every((document) => !!document.sentPdfUrl);
  // The contract that brings the owner questionnaire (a seller pair: the order, not the exclusivity).
  const questionnaireCarrier = documents.find((document) => carriesQuestionnaireStep(document));
  const questionnaireContext = questionnaireCarrier
    ? questionnaireLabelContext(questionnaireListedForOf(questionnaireCarrier))
    : undefined;

  const markShared = (ids: string[]) =>
    setSharedByRealtor((current) => Array.from(new Set([...current, ...ids])));
  const markAll = (state: Delivery) =>
    setDelivery((current) => ({ ...current, ...Object.fromEntries(withLink.map((document) => [document.id, state])) }));

  async function sendSet() {
    if (inFlight.current || !entry || allSent) return;
    inFlight.current = true;
    setBusy("message");
    setError(null);
    setNotice(null);
    setConfirmingClose(false);
    markAll("sending");
    try {
      const sent = await sendSigningSet(entry.id, entry.signers[0].id);
      // Every contract the message covered carries that one delivery; WhatsApp accepted is success,
      // the same rule as the single-contract dialog (the SMS goes with it either way).
      const byId = new Map(sent.documents.map((document) => [document.id, document]));
      setDelivery((current) => ({
        ...current,
        ...Object.fromEntries(withLink.map((document) => {
          const signer = byId.get(document.id)?.signers.find((item) => item.id === document.signers[0].id);
          const covered = sent.coveredDocumentIds.includes(document.id);
          return [document.id, covered && signer?.whatsappDelivery?.status === "sent" ? "sent" : "failed"];
        }))
      }));
      if (sent.whatsapp === "sent") {
        setNotice(t("signatures.prepared.setMessageSent", { to: deliveryRecipientLabel(entry.signers[0]), count: sent.coveredDocumentIds.length }));
      } else {
        setError(t("signatures.prepared.setMessageError"));
      }
    } catch (failure) {
      const refusal = refusalOf(failure);
      if (refusal === "in_progress" || refusal === "just_sent") {
        // Another press of this same set (another tab, a double submit) is sending or just sent it.
        markAll("idle");
        setNotice(t("signatures.prepared.setSendBusy"));
      } else {
        markAll("failed");
        setError(t(refusal === "nothing_to_sign" ? "signatures.prepared.setNothingToSign" : "signatures.prepared.setMessageError"));
      }
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  async function copyLink() {
    if (inFlight.current || !share) return;
    inFlight.current = true;
    setBusy("copy");
    setError(null);
    setNotice(null);
    try {
      await navigator.clipboard.writeText(share.text);
      markShared(withLink.map((document) => document.id));
      setNotice(t("signatures.prepared.copiedSet", { count: share.documentCount }));
    } catch {
      setError(t("signatures.prepared.copyError"));
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  // ONE printable file for the set: every contract's prepared PDF, in the set's order.
  async function downloadSet() {
    if (inFlight.current || !canDownload) return;
    inFlight.current = true;
    setBusy("download");
    setError(null);
    setNotice(null);
    try {
      const parts = await Promise.all(documents.map((document) => downloadPreparedPdfBlob(document.id)));
      const merged = await mergePdfBlobs(parts);
      saveFile(merged, `${documents.map((document) => document.formattedNumber || document.id).join("+")}.pdf`);
    } catch {
      setError(t("signatures.prepared.downloadError"));
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  }

  // Backdrop, Escape and "סיום" all pass through here; only "סיום בלי לשלוח" closes past the warning.
  function requestClose() {
    if (inFlight.current) return;
    if (mustConfirmClose) setConfirmingClose(true);
    else onClose();
  }

  return <Dialog open onClose={requestClose} fullWidth maxWidth="sm">
    <DialogTitle>{t("signatures.prepared.setTitle")}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Box>
          {/* One mention up top that the one link also leads to the questionnaire; the line under the
              contract that carries it says when. */}
          <Typography fontWeight={700}>
            {questionnaireCarrier
              ? t("signatures.prepared.setOneLinkQuestionnaire", { count: documents.length, context: questionnaireContext })
              : t("signatures.prepared.setOneLink", { count: documents.length })}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>{t("signatures.prepared.setChooseAction")}</Typography>
        </Box>
        {!!result.failedDocumentCount && <Alert severity="warning">
          {t("signatures.prepared.partialFailure", { count: result.failedDocumentCount })}
        </Alert>}
        {/* The contracts the one link opens: lines, not cards — nothing here acts on one contract alone. */}
        <Box component="ul" aria-label={t("signatures.prepared.setDocuments")}
          sx={{ listStyle: "none", m: 0, p: 0, border: 1, borderColor: "divider", borderRadius: 1 }}>
          {documents.map((document, index) => {
            const state = delivery[document.id];
            return <Box component="li" key={document.id} data-document-id={document.id}
              sx={{ px: 1.5, py: 1.25, ...(index ? { borderTop: 1, borderColor: "divider" } : {}) }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 1 }} alignItems="flex-start" justifyContent="space-between">
                <Typography fontWeight={600} sx={{ minWidth: 0, overflowWrap: "break-word" }}>
                  {document.templateNameSnapshot}
                  {/* " · C-13" stays one unit on a narrow screen: never a lone "·" ending a line. */}
                  <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                    {" · "}
                    <bdi>{document.formattedNumber}</bdi>
                  </Box>
                </Typography>
                <Label color={DELIVERY_COLOR[state]} sx={{ flexShrink: 0, gap: 0.5 }}>
                  {state === "sending" && <CircularProgress size={12} thickness={5} color="inherit" />}
                  {t(`signatures.prepared.delivery.${state}`)}
                </Label>
              </Stack>
              {/* WHICH contract brings the questionnaire, and when the client meets it. */}
              {carriesQuestionnaireStep(document) && (
                <QuestionnaireRidesHere listedFor={questionnaireListedForOf(document)} sx={{ mt: 0.5 }} />
              )}
            </Box>;
          })}
        </Box>
        {notice && <Alert severity="success">{notice}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <Button fullWidth variant="contained" size="large"
          endIcon={busy === "message" ? <CircularProgress size={18} color="inherit" /> : <ForwardSendIcon />}
          disabled={!!busy || !entry || allSent} onClick={sendSet}>
          {t("signatures.prepared.sendMessage")}
        </Button>
        {/* The realtor's own WhatsApp: ONE link for the whole set, with a line saying how many documents
            it opens. A tap counts as shared, so finishing afterwards is not warned about. */}
        <SigningLinkWhatsappShareButton documents={withLink} fullWidth size="large" showHint disabled={!!busy}
          onShared={() => markShared(withLink.map((document) => document.id))} />
        <Button fullWidth variant="outlined" size="large" startIcon={<ContentCopy />}
          disabled={!!busy || !share} onClick={copyLink}>
          {t("signatures.prepared.copyLink")}
        </Button>
        <Stack spacing={0.5}>
          <Button fullWidth variant="outlined" size="large" startIcon={<OpenInNew />}
            component="a" href={entryLink} target="_blank" rel="noopener noreferrer" disabled={!!busy || !entryLink}
            onClick={() => markShared(withLink.map((document) => document.id))}>
            {t("signatures.prepared.signInPerson")}
          </Button>
          <Typography variant="caption" color="text.secondary">{t("signatures.prepared.signInPersonSetHint")}</Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Button fullWidth variant="outlined" size="large"
            startIcon={busy === "download" ? <CircularProgress size={18} /> : <Download />}
            disabled={!!busy || !canDownload} onClick={downloadSet}>
            {t("signatures.prepared.downloadPdf")}
          </Button>
          <Typography variant="caption" color="text.secondary">{t("signatures.prepared.downloadSetHint", { count: documents.length })}</Typography>
        </Stack>
      </Stack>
    </DialogContent>
    {confirmingClose && mustConfirmClose ? <DialogActions disableSpacing sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, px: 3, pb: 2 }}>
      <Alert severity="warning">
        {t("signatures.prepared.unsentWarning", { titles: notDelivered.map(documentLabel).join(", ") })}
      </Alert>
      <Stack direction="row" flexWrap="wrap" justifyContent="flex-end" sx={{ gap: 1 }}>
        <Button onClick={onClose}>{t("signatures.prepared.closeAnyway")}</Button>
        {!allSent && entry && <Button variant="contained" endIcon={<ForwardSendIcon />} onClick={sendSet}>
          {t("signatures.prepared.sendMessage")}
        </Button>}
      </Stack>
    </DialogActions> : <DialogActions>
      <Button disabled={!!busy} onClick={requestClose}>{t("signatures.prepared.close")}</Button>
    </DialogActions>}
  </Dialog>;
}
